import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AutocompleteDropdown,
  Box,
  Controller,
  ControllerWrapper,
  DateInput,
  FieldWrapper,
  formatSnakeCaseToGeneral,
  FormFieldsContainer,
  Label,
  RegularField,
  useFormContext,
  useResponsive,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';

import { IAuthorizedUserDtoRelationshipTypeEnum } from '@/dtos';

import { AuthorizedUserFormValues, FormFields } from './hooks/useAuthorizedUserForm';

const RELATIONSHIP_OPTIONS = Object.values(IAuthorizedUserDtoRelationshipTypeEnum).map((value) => ({
  key: value,
  value,
  title: capitalize(formatSnakeCaseToGeneral(value)),
}));

const LABEL_WIDTH = { width: '100px', min: '100px' };
const INPUT_WIDTH = '320px';

const AuthorizedUsersForm = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { control, formState: { errors } } = useFormContext<AuthorizedUserFormValues>();

  return (
    <FormFieldsContainer>
      <FieldWrapper>
        <Box direction={isMobile ? 'column' : 'row'} gap={isMobile ? 'spacing12' : undefined} align={isMobile ? undefined : 'center'} justify={isMobile ? undefined : 'between'} width="100%">
          <Label name={FormFields.firstName} showRequireIcon width={isMobile ? undefined : LABEL_WIDTH}>
            {t('First name')}
          </Label>
          <ControllerWrapper style={isMobile ? { width: '100%' } : { flex: 'none' }} errorText={errors?.[FormFields.firstName]?.message}>
            <Controller
              name={FormFields.firstName}
              control={control}
              render={({ field }) => (
                <RegularField
                  name={FormFields.firstName}
                  testId="authorized-users-modal_first-name"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t('Enter first name')}
                  isError={!!errors?.[FormFields.firstName]?.message}
                  style={{ width: isMobile ? '100%' : INPUT_WIDTH }}
                  editMode
                />
              )}
            />
          </ControllerWrapper>
        </Box>
      </FieldWrapper>

      <FieldWrapper>
        <Box direction={isMobile ? 'column' : 'row'} gap={isMobile ? 'spacing12' : undefined} align={isMobile ? undefined : 'center'} justify={isMobile ? undefined : 'between'} width="100%">
          <Label name={FormFields.lastName} showRequireIcon width={isMobile ? undefined : LABEL_WIDTH}>
            {t('Last name')}
          </Label>
          <ControllerWrapper style={isMobile ? { width: '100%' } : { flex: 'none' }} errorText={errors?.[FormFields.lastName]?.message}>
            <Controller
              name={FormFields.lastName}
              control={control}
              render={({ field }) => (
                <RegularField
                  name={FormFields.lastName}
                  testId="authorized-users-modal_last-name"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t('Enter last name')}
                  isError={!!errors?.[FormFields.lastName]?.message}
                  style={{ width: isMobile ? '100%' : INPUT_WIDTH }}
                  editMode
                />
              )}
            />
          </ControllerWrapper>
        </Box>
      </FieldWrapper>

      <FieldWrapper>
        <Box direction={isMobile ? 'column' : 'row'} gap={isMobile ? 'spacing12' : undefined} align={isMobile ? undefined : 'center'} justify={isMobile ? undefined : 'between'} width="100%">
          <Label name={FormFields.relationshipType} showRequireIcon width={isMobile ? undefined : LABEL_WIDTH}>
            {t('Relationship')}
          </Label>
          <ControllerWrapper style={isMobile ? { width: '100%' } : { flex: 'none' }}>
            <Controller
              name={FormFields.relationshipType}
              control={control}
              render={({ field }) => (
                <AutocompleteDropdown
                  name={FormFields.relationshipType}
                  testId="authorized-users-modal_relationship-type"
                  onChange={field.onChange}
                  options={RELATIONSHIP_OPTIONS}
                  value={field.value}
                  defaultValue={field.value}
                  placeholder={t('Choose relationship')}
                  errorText={errors?.[FormFields.relationshipType]?.message}
                  style={{ width: isMobile ? '100%' : INPUT_WIDTH }}
                />
              )}
            />
          </ControllerWrapper>
        </Box>
      </FieldWrapper>

      <FieldWrapper>
        <Box direction={isMobile ? 'column' : 'row'} gap={isMobile ? 'spacing12' : undefined} align={isMobile ? undefined : 'center'} justify={isMobile ? undefined : 'between'} width="100%">
          <Label name={FormFields.birthOn} showRequireIcon width={isMobile ? undefined : LABEL_WIDTH}>
            {t('Date of Birth')}
          </Label>
          <ControllerWrapper
            style={isMobile ? { width: '100%' } : { flex: 'none', width: INPUT_WIDTH }}
            errorText={errors?.[FormFields.birthOn]?.message}
          >
            <Controller
              name={FormFields.birthOn}
              control={control}
              render={({ field }) => (
                <DateInput
                  name={FormFields.birthOn}
                  testId="authorized-users-modal_birth-on"
                  maxDate={dayjs()}
                  isManualDateInput
                  selectDateWithoutApply
                  placeholder="MM/DD/YYYY"
                  onChange={field.onChange}
                  value={field.value}
                  isError={!!errors?.[FormFields.birthOn]?.message}
                  style={{ width: '100%' }}
                  isPicker
                />
              )}
            />
          </ControllerWrapper>
        </Box>
      </FieldWrapper>
    </FormFieldsContainer>
  );
};

export default AuthorizedUsersForm;
